import type { PieceModel } from '@doer/frontend-components/creativity-editor';
import { buildScenesModel } from './build-scenes-model';
import { parseSceneRefFromUrl } from './scene-url-utils';
import type { AvatarsScene } from '../hooks/use-avatars-project';
import type { AvatarsSubtitleStyle } from '../types/subtitles';

type ModelLayer = PieceModel['layers'][string];
type Dimension = { width: number; height: number };

export type SmartMergeOptions = {
  projectId: string;
  scenes: AvatarsScene[];
  dimension: Dimension;
  outro?: { videoUrl: string; durationMs: number };
  music?: { trackId: string; trackName: string; audioUrl: string };
  subtitleStyle?: AvatarsSubtitleStyle;
};

/**
 * Clasifica un layer como scene-derived (nace de `buildScenesModel`) o user-added.
 *
 * Reglas para scene-derived (siempre regenerated):
 *   - `kind === 'pill'`: subtítulos, nacen de VTT cues del script actual — identificados por el
 *     flag explícito `sceneDerived` (estampado en `build-scenes-model.ts`), no por el `kind` solo:
 *     un pill agregado a mano en el editor es del mismo `kind` y debe preservarse. Ver
 *     docs/creativity-editor-modelo/bugs/1-pill-manual-desaparece-en-smart-merge.md.
 *   - `kind === 'video'` con URL matching /videos/scene-<uuid>.mp4  → video de scene.
 *   - `kind === 'video'` con URL matching /placas/                 → outro placa.
 *   - `kind === 'audio'` con URL matching /tts/scene-<uuid>.wav    → TTS voz de scene.
 *   - `kind === 'audio'` con `content.source === 'library'` **y `content.origin === 'studio'`**
 *     → música canónica del proyecto (viene de Studio). La música con `origin === 'editor'`
 *     (agregada desde el sound-panel) se considera user-added.
 *
 * Fallback para pieces guardados antes de que existiera `origin`: si no hay marker,
 * usar matching por `trackId` contra el track actual de Studio.
 *
 * Todo lo demás se considera user-added y se preserva intacto (posición, contenido, timing).
 */
const isSceneDerivedLayer = (layer: ModelLayer, studioTrackId: string | undefined): boolean => {
  const anyLayer = layer as {
    id?: string;
    kind?: string;
    sceneDerived?: boolean;
    content?: { src?: unknown; source?: unknown; trackId?: unknown; origin?: unknown };
  };
  const kind = anyLayer.kind;
  const src = anyLayer.content?.src;
  const source = anyLayer.content?.source;

  if (kind === 'pill') {
    if (anyLayer.sceneDerived !== undefined) return anyLayer.sceneDerived === true;
    // Piece guardado antes de que existiera `sceneDerived`: los subtítulos de escena siempre
    // nacieron con generateUniqueId('pill') (id con prefijo `pill_`); un pill agregado a mano
    // recibe un uuid sin prefijo (getOrAssignLayerId). Este respaldo deja de usarse para esta
    // pieza en cuanto se vuelva a guardar con el flag ya presente.
    return typeof anyLayer.id === 'string' && anyLayer.id.startsWith('pill_');
  }

  if (kind === 'video' && typeof src === 'string') {
    if (parseSceneRefFromUrl(src)) return true;
    if (src.includes('/placas/')) return true;
    return false;
  }

  if (kind === 'audio') {
    if (typeof src === 'string' && parseSceneRefFromUrl(src)) return true;
    if (source === 'library') {
      const origin = anyLayer.content?.origin;
      if (origin === 'studio') return true;
      if (origin === 'editor') return false;
      // Fallback
      if (studioTrackId !== undefined) {
        const trackId = anyLayer.content?.trackId;
        return typeof trackId === 'string' && trackId === studioTrackId;
      }
    }
    return false;
  }

  return false;
};

/**
 * Reconcilia el piece guardado en S3 con el estado actual de scenes.
 *
 * Usa el layerOrder guardado como base y reemplaza los IDs scene-derived por sus equivalentes
 * nuevos mediante un mapeo por sceneId + posición.
 * Las capas del usuario quedan exactamente donde estaban guardadas.
 * Los IDs no colisionan porque `buildScenesModel` usa `generateUniqueId(kind)` con
 * session prefix propio.
 */
/** Extrae el sceneId de un layer scene-derived (video/audio), o null. */
const getSceneId = (layer: ModelLayer): string | null => {
  const src = (layer as { content?: { src?: unknown } }).content?.src;
  if (typeof src !== 'string') return null;
  const ref = parseSceneRefFromUrl(src);
  return ref ? ref.sceneId : null;
};

const isPillLayer = (layer: ModelLayer): boolean => (layer as { kind?: string }).kind === 'pill';

const isPlacaLayer = (layer: ModelLayer): boolean => {
  const anyLayer = layer as { kind?: string; content?: { src?: unknown } };
  if (anyLayer.kind !== 'video') return false;
  const src = anyLayer.content?.src;
  return typeof src === 'string' && src.includes('/placas/');
};

export const smartMergePiece = (s3Piece: PieceModel, options: SmartMergeOptions): PieceModel => {
  const fresh = buildScenesModel({
    projectId: options.projectId,
    scenes: options.scenes,
    dimension: options.dimension,
    outro: options.outro,
    music: options.music,
    subtitleStyle: options.subtitleStyle
  });

  // 1. Recolectar capas user-added del piece guardado. Se pasa el trackId de la música
  //    canónica de Studio (si hay) para que música extra agregada por el user desde el
  //    sound-panel del editor NO sea confundida con la canónica y descartada.
  const studioTrackId = options.music?.trackId;
  const userLayers: Record<string, ModelLayer> = {};
  const userVisualIds = new Set<string>();
  for (const [id, layer] of Object.entries(s3Piece.layers)) {
    if (isSceneDerivedLayer(layer, studioTrackId)) continue;
    userLayers[id] = layer;
    if ((layer as { kind?: string }).kind !== 'audio') {
      userVisualIds.add(id);
    }
  }

  // 2. Indexar contenido scene-derived del modelo viejo y del fresco, agrupado por sceneId.
  const indexSceneContent = (layerOrder: string[], layers: Record<string, ModelLayer>, skipIds: Set<string>) => {
    const byScene = new Map<string, { video: string; pills: string[] }>();
    let placa: string | null = null;
    let currentScene: string | null = null;

    for (const id of layerOrder) {
      if (skipIds.has(id)) continue;
      const layer = layers[id];
      if (!layer) continue;

      if (isPlacaLayer(layer)) {
        placa = id;
        continue;
      }

      const sceneId = getSceneId(layer);
      if (sceneId) {
        currentScene = sceneId;
        if (!byScene.has(sceneId)) byScene.set(sceneId, { video: '', pills: [] });
        byScene.get(sceneId)!.video = id;
        continue;
      }

      if (isPillLayer(layer) && currentScene) {
        byScene.get(currentScene)!.pills.push(id);
      }
    }

    return { byScene, placa };
  };

  const oldIdx = indexSceneContent(s3Piece.layerOrder ?? [], s3Piece.layers, userVisualIds);
  const freshIdx = indexSceneContent(fresh.layerOrder, fresh.layers, new Set<string>());

  // 3. Armar map old ID → fresh ID para scenes compartidas (video y pills, por sceneId + index).
  const idMap = new Map<string, string>();
  if (oldIdx.placa && freshIdx.placa) idMap.set(oldIdx.placa, freshIdx.placa);

  const oldSceneIds = Array.from(oldIdx.byScene.keys());
  for (const sceneId of oldSceneIds) {
    const oldEntry = oldIdx.byScene.get(sceneId)!;
    const freshEntry = freshIdx.byScene.get(sceneId);
    if (!freshEntry) continue; // scene borrada
    idMap.set(oldEntry.video, freshEntry.video);
    const n = Math.min(oldEntry.pills.length, freshEntry.pills.length);
    for (let i = 0; i < n; i++) idMap.set(oldEntry.pills[i], freshEntry.pills[i]);
  }

  // 4. Caminar el layerOrder guardado: user layers quedan en su lugar.
  const mergedOrder: string[] = [];
  const placed = new Set<string>();

  for (const id of s3Piece.layerOrder ?? []) {
    if (userVisualIds.has(id)) {
      mergedOrder.push(id);
      continue;
    }
    const mapped = idMap.get(id);
    if (mapped) {
      mergedOrder.push(mapped);
      placed.add(mapped);
    }
    // sin mapping → scene borrada o pills que se redujeron → descartar
  }

  // 5. Insertar contenido nuevo no colocado (scenes nuevas, pills extras, placa nueva).
  for (let fi = 0; fi < fresh.layerOrder.length; fi++) {
    const freshId = fresh.layerOrder[fi];
    if (placed.has(freshId)) continue;
    const layer = fresh.layers[freshId];
    if (!layer) continue;

    // Placa sin contraparte vieja: al principio (capa más atrás del z-stack)
    if (isPlacaLayer(layer)) {
      mergedOrder.unshift(freshId);
      placed.add(freshId);
      continue;
    }

    // Buscar el fresh ID anterior que ya esté colocado → insertar después de él.
    let insertIdx = mergedOrder.length; // fallback: al final (frente del z-stack)
    for (let i = fi - 1; i >= 0; i--) {
      const prevFreshId = fresh.layerOrder[i];
      if (placed.has(prevFreshId)) {
        insertIdx = mergedOrder.indexOf(prevFreshId) + 1;
        break;
      }
    }
    mergedOrder.splice(insertIdx, 0, freshId);
    placed.add(freshId);
  }

  // 6. Ajustar duración total si algún user layer se extiende más allá del material de scenes.
  let maxEnd = fresh.duration;
  for (const layer of Object.values(userLayers)) {
    const start = (layer as { start?: number }).start ?? 0;
    const duration = (layer as { duration?: number }).duration ?? 0;
    maxEnd = Math.max(maxEnd, start + duration);
  }

  return {
    ...fresh,
    layers: { ...fresh.layers, ...userLayers },
    layerOrder: mergedOrder,
    duration: maxEnd
  };
};
